/**
 * 用户处理数据内容
 * create by sevencai@20180828
 * contact sevencai@tencent.com
 */
import getSubscribes from '@/api/subscribe'
import { getUserAgent } from '@tencent/midas-util'

/**
 * 是否在安卓微信上展示改商品
 */
export function shouldDisplay(item: { platform?: Platform[] }) {
  const ua = getUserAgent()

  let platform = ''

  if (ua.iOS) {
    platform = 'ios'
  } else {
    platform = 'android'
  }

  if (ua.QQ) {
    platform += '_qq'
  } else {
    platform += '_wx'
  }

  return item.platform && item.platform.includes(platform as Platform)
}

/**
 * 排序 并且 过滤数据
 * @param arr
 */
function getSortedFilteredData<T extends FilterableItem>(arr: T[]) {
  return arr.filter(item => (item.status === '1' || item.status === true) && shouldDisplay(item)).sort((a, b) => a.order - b.order)
}

/**
 * 若商品列表里存在已订阅的内容
 * 则已订阅的内容放在前部显示
 */
export async function sortByGroupId(arr) {
  let subscribes = []

  try {
    subscribes = await getSubscribes()
  } catch (e) {
    return getSortedFilteredData(arr)
  }

  // 若用户没有订阅过
  if (!subscribes || !subscribes.length) {
    return getSortedFilteredData(arr)
  }

  // 对 arr 进行 sort 排序，并且订阅业务放在前部
  if (arr && arr.length) {
    arr.forEach((item, index) => {
      if (item.groupId && subscribes.indexOf(item.groupId.toString()) !== -1) {
        arr.splice(index, 1)
        arr.unshift(item)
      }
    })
  }

  // 剔除掉下线的业务
  return getSortedFilteredData(arr)
}

/**
 * 获取服务器时间
 * 若服务器时间没有取到则使用客户端时间
 * 理论上部署到线上不应该存在取不到服务器时间的情况
 */
export function getServerTime(): number {
  // eslint-disable-next-line no-restricted-properties
  return !window.isNaN(window.__serverTime) ? window.__serverTime : new Date().getTime()
}

/**
 * 去除活动列表里活动截止时间已过期的活动
 * 获取活动列表里开始时间最早的活动
 * 认为最早开始的活动为最近一次的时间
 */
export function getMinimumTimeActivity<T extends Timed>(config: T[]): T {
  const serverTime = getServerTime()
  const arr = config.filter(item => new Date(item.endTime).getTime() >= serverTime)

  if (!arr || !arr.length) {
    // FIXME
    return {} as T
  }

  return arr.reduce((pre, cur) => {
    const a = new Date(pre.beginTime).getTime()
    const b = new Date(cur.beginTime).getTime()

    if (a > b) {
      return cur
    }

    return pre
  })
}

/**
 * 检测当前是否在活动范围内
 * @param activity 活动时间
 */
export function checkInActivityTime(activity: Timed) {
  const serverTime = getServerTime()
  const beginTime = new Date(activity.beginTime).getTime()
  const endTime = new Date(activity.endTime).getTime()

  if (serverTime >= beginTime && serverTime <= endTime) {
    return true
  }

  return false
}

/**
 * 获得可供展示的福利列表
 *
 * 1. 根据 status 过滤非上线状态的活动，status = 1 为上线状态
 * 2. 根据 order 排序，order 越大代表越靠后
 * 3. 根据 beginTime 和 endTime 判断是否在活动时间范围内
 *    当任意一项不指定时，代表不限制该项
 */
export function getActiveList<T extends FilterableItem>(list: T[]): T[] {
  return getSortedFilteredData(list).filter(item => {
    const serverTime = getServerTime()
    // 获取开始时间/结束时间
    // 当该字段不存在时返回 NaN
    const beginTime = new Date(item.beginTime).getTime()
    const endTime = new Date(item.endTime).getTime()

    // 当不存在 beginTime 时立即展示
    // 当不存在 endTime 时不终止展示
    return (Number.isNaN(beginTime) || serverTime >= beginTime) && (Number.isNaN(endTime) || serverTime <= endTime)
  })
}

/**
 * 获得礼包天天领的列表
 *
 * status = true 为上线状态
 */
export function getGiftList(list: DailyItem[]) {
  return (
    list
    && list
      .filter(item => item && item.giftConfig && (item.giftConfig.status as any) * 1 === 1)
      .filter(item => shouldDisplay(item))
      .map(item => {
        const subTitle = item.giftConfig.subTitleText && item.giftConfig.subTitleText.split('$$')
        const groupId = item.giftConfig.groupId_busId && item.giftConfig.groupId_busId.split('_')[0]

        return {
          ...item,
          ...item.giftConfig,
          subTitle,
          groupId
        }
      })
  )
}

type Unpacked<T> = T extends (infer U)[] ? U : T
export type GiftItem = Unpacked<ReturnType<typeof getGiftList>>
